import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Code } from 'lucide-react';
import { useTranslation } from '../contexts/LanguageContext';

interface NavbarProps {
  scrolled: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ scrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, lang, toggleLang } = useTranslation();

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const navItems = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.projects'), path: '/projects' },
    { name: t('nav.skills'), path: '/skills' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-transparent backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-6">
          <Link 
            to="/" 
            className="flex items-center space-x-2 text-white hover:text-sky-600 transition-colors"
            onClick={closeMenu}
          >
            <Code size={28} strokeWidth={1.5} />
            <span className="text-2xl font-bold">{t('nav.brand')}</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  font-semibold text-lg transition-colors relative
                  ${isActive 
                    ? 'text-sky-700 after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-0.5 after:bg-sky-500' 
                    : 'text-white hover:text-sky-600'
                  }
                `}
              >
                {item.name}
              </NavLink>
            ))}

            <button
              type="button"
              onClick={toggleLang}
              aria-label={t('nav.lang.toggleLabel')}
              className="ml-4 px-3 py-1 rounded-full border border-primary-200 text-sm font-semibold text-white hover:bg-sky-600 transition-colors"
            >
              {lang === 'en' ? `${t('nav.lang.en')} / ${t('nav.lang.fr')}` : `${t('nav.lang.fr')} / ${t('nav.lang.en')}`}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white hover:text-sky-600 transition-colors"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-transparent backdrop-blur-md shadow-lg"
          >
            <nav className="flex flex-col px-4 py-4 space-y-4">
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={toggleLang}
                  aria-label={t('nav.lang.toggleLabel')}
                  className="px-3 py-1 rounded-full border border-primary-200 text-sm font-semibold text-white transition-colors hover:bg-sky-600"
                >
                  {lang === 'en'
                    ? `${t('nav.lang.en')} / ${t('nav.lang.fr')}`
                    : `${t('nav.lang.fr')} / ${t('nav.lang.en')}`}
                </button>
              </div>
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `
                    text-base font-medium py-2 px-4 rounded-md transition-colors
                    ${isActive 
                      ? 'bg-sky-50 text-sky-700' 
                      : 'text-primary-700 hover:bg-primary-50 hover:text-sky-600'
                    }
                  `}
                  onClick={closeMenu}
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;